/**
 * 选择管理器Hook - 封装机场和供应商选择相关的逻辑
 * 
 * 职责：
 * - 管理机场和供应商的选择状态
 * - 处理机场和供应商的onChange事件
 * - 调用API获取可用的供应商和产品
 * - 自动选择逻辑（单一选项时自动选择）
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { message } from 'antd';
import { useBatchTransactionAPI } from './useBatchTransactionAPI';

export const useSelectionManager = (initialAirports = [], transactionType) => {
  // 选择状态
  const [activeAirports, setActiveAirports] = useState([]);
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedContractItems, setSelectedContractItems] = useState([]);

  // 加载状态
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // 跟踪上一次的参数，用于检测变更
  const prevTransactionTypeRef = useRef(transactionType);
  const prevInitialAirportsRef = useRef(initialAirports);

  const { getAvailableProductsByAirports } = useBatchTransactionAPI();

  // 清除错误
  const clearError = useCallback((key) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[key];
      return newErrors;
    });
  }, []);

  // 设置错误
  const setError = useCallback((key, error) => {
    setErrors(prev => ({
      ...prev,
      [key]: error
    }));
  }, []);

  // 处理机场选择变更
  const handleAirportChange = useCallback((checkedAirports) => {
    setActiveAirports(checkedAirports);
    
    // 清空供应商和产品选择，因为机场变更会影响可用选项
    setSelectedSupplier(null);
    setAvailableSuppliers([]);
    setAvailableProducts([]);
    setSelectedContractItems([]);
    
    clearError('suppliers');
    clearError('products');
  }, [clearError]);

  // 全选/取消全选机场
  const handleSelectAllAirports = useCallback((checked) => {
    const newSelection = checked ? [...initialAirports] : [];
    handleAirportChange(newSelection);
  }, [initialAirports, handleAirportChange]);

  // 加载产品并派生供应商列表
  const loadProductsAndSuppliers = useCallback(async () => {
    if (!activeAirports.length || !transactionType) return;

    setSuppliersLoading(true);
    try {
      const products = await getAvailableProductsByAirports(transactionType, activeAirports);
      setAvailableProducts(products || []);

      // 从产品中派生供应商列表
      const supplierMap = new Map();
      (products || []).forEach((item) => {
        if (item.partner_id && item.partner_name) {
          supplierMap.set(item.partner_id, { 
            id: item.partner_id, 
            name: item.partner_name 
          });
        }
      });
      const suppliers = Array.from(supplierMap.values());
      setAvailableSuppliers(suppliers);

      // 只有一个供应商时自动选择
      if (suppliers.length === 1) {
        const onlySupplier = suppliers[0];
        setSelectedSupplier(onlySupplier);
        const onlySupplierProducts = (products || []).filter(p => p.partner_id === onlySupplier.id);
        if (onlySupplierProducts.length === 1) {
          setSelectedContractItems([onlySupplierProducts[0]]);
          message.info('已自动添加唯一的供应商和产品');
        }
      }

      clearError('suppliers');
    } catch (error) {
      console.error('加载产品/供应商失败:', error);
      setError('suppliers', error.message);
      message.error('加载产品/供应商失败: ' + error.message);
    } finally {
      setSuppliersLoading(false);
    }
  }, [activeAirports, transactionType, getAvailableProductsByAirports, clearError, setError]);

  // 处理供应商选择变更
  const handleSupplierChange = useCallback(async (supplierId) => {
    const supplier = availableSuppliers.find(s => s.id === supplierId) || null;
    setSelectedSupplier(supplier);

    // 基于已加载的产品过滤
    let supplierProducts = [];
    if (supplier) {
      supplierProducts = (availableProducts || []).filter(p => p.partner_id === supplier.id);
    }

    if (supplier && supplierProducts.length === 1) {
      setSelectedContractItems([supplierProducts[0]]);
      message.info('检测到该供应商只有一个产品，已自动选择');
    } else {
      setSelectedContractItems([]);
    }

    clearError('products');
  }, [availableSuppliers, availableProducts, clearError]);

  // 当事务类型或航班变更时，重置所有选择状态
  useEffect(() => {
    // 创建当前参数的标识
    const currentAirportsKey = initialAirports.map(a => a).sort().join(',');
    const prevAirportsKey = prevInitialAirportsRef.current.map(a => a).sort().join(',');

    const transactionTypeChanged = prevTransactionTypeRef.current !== transactionType;
    const airportsChanged = currentAirportsKey !== prevAirportsKey;

    if (transactionTypeChanged || airportsChanged) {
      console.log('🔄 参数变更，重置选择状态:', {
        transactionTypeChanged,
        airportsChanged,
        transactionType: { from: prevTransactionTypeRef.current, to: transactionType },
        airports: { from: prevInitialAirportsRef.current, to: initialAirports }
      });

      // 重置所有选择相关的状态
      setActiveAirports([]);
      setAvailableSuppliers([]);
      setSelectedSupplier(null);
      setAvailableProducts([]);
      setSelectedContractItems([]);
      setSuppliersLoading(false);
      setErrors({});

      // 更新引用
      prevTransactionTypeRef.current = transactionType;
      prevInitialAirportsRef.current = initialAirports;
    }
  }, [transactionType, initialAirports]);

  // 当机场选择变更时，重新加载供应商
  useEffect(() => {
    if (activeAirports.length > 0) {
      loadProductsAndSuppliers();
    } else {
      // 无机场时清空
      setAvailableProducts([]);
      setAvailableSuppliers([]);
      setSelectedSupplier(null);
      setSelectedContractItems([]);
    }
  }, [activeAirports, loadProductsAndSuppliers]);

  // 计算全选状态
  const isAllAirportsSelected = initialAirports.length > 0 && activeAirports.length === initialAirports.length;
  const isIndeterminateAirports = activeAirports.length > 0 && activeAirports.length < initialAirports.length;

  return {
    // 状态
    activeAirports,
    availableSuppliers,
    selectedSupplier,
    availableProducts,
    selectedContractItems,
    suppliersLoading,
    errors,
    
    // 计算属性
    isAllAirportsSelected,
    isIndeterminateAirports,
    
    // 操作方法
    handleAirportChange,
    handleSelectAllAirports,
    handleSupplierChange,
    setSelectedContractItems,
    loadProductsAndSuppliers,
    clearError,
    setError
  };
};
